/**
 * AURA v2 Command Palette — ⌘K / Ctrl+K global launcher.
 *
 * Sources (static for Phase 2 — backend-driven results arrive in Phase 6):
 *   - Navigation destinations (top sidebar links across all roles)
 *   - Recent/suggested AI actions (credit cost shown)
 *   - Quick create actions (→ navigate to /something/new)
 *
 * Keyboard:
 *   ↑ ↓      navigate
 *   Enter    select
 *   Esc      close
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Target, Receipt, Users, Building2, Sparkles, Plus, FileText,
  ShoppingCart, BookOpen, Handshake, Package, Factory, Coins, FileSignature,
  Truck, Wrench, BarChart3, Wallet, CalendarDays, Headphones, Globe,
} from 'lucide-react';

type ItemKind = 'Navigate' | 'AI' | 'Create' | 'Recent';
type LucideIcon = typeof Search;

interface PaletteItem {
  kind: ItemKind;
  label: string;
  to?: string;
  icon: LucideIcon;
  meta?: string;
  hint?: string;
  /** Credit cost for AI actions */
  cost?: number;
}

const STATIC_ITEMS: PaletteItem[] = [
  // Navigation
  { kind: 'Navigate', label: 'Dashboard', icon: BarChart3, to: '/dashboard' },
  { kind: 'Navigate', label: 'Deals', icon: Handshake, to: '/deals', hint: 'G D' },
  { kind: 'Navigate', label: 'Contacts', icon: Users, to: '/contacts' },
  { kind: 'Navigate', label: 'Customers', icon: Building2, to: '/customers' },
  { kind: 'Navigate', label: 'Accounts', icon: Building2, to: '/accounts' },
  { kind: 'Navigate', label: 'Proposals', icon: FileText, to: '/proposals' },
  { kind: 'Navigate', label: 'Sales Orders', icon: ShoppingCart, to: '/sales-orders' },
  { kind: 'Navigate', label: 'Invoices', icon: Receipt, to: '/invoices', hint: 'G I' },
  { kind: 'Navigate', label: 'Payments', icon: Wallet, to: '/payments' },
  { kind: 'Navigate', label: 'Expenses', icon: Wallet, to: '/expenses' },
  { kind: 'Navigate', label: 'Accounting (Ledger)', icon: BookOpen, to: '/accounting' },
  { kind: 'Navigate', label: 'GST Filing', icon: Globe, to: '/gst/filing' },
  { kind: 'Navigate', label: 'e-Invoices', icon: FileSignature, to: '/gst/e-invoices' },
  { kind: 'Navigate', label: 'e-Way Bills', icon: Truck, to: '/gst/e-waybills' },
  { kind: 'Navigate', label: 'Purchase Orders', icon: ShoppingCart, to: '/purchase-orders' },
  { kind: 'Navigate', label: 'Vendors', icon: Truck, to: '/vendors' },
  { kind: 'Navigate', label: 'Quotation Jobs', icon: FileSignature, to: '/quotation-jobs' },
  { kind: 'Navigate', label: 'Inventory', icon: Package, to: '/inventory' },
  { kind: 'Navigate', label: 'Inward Dashboard', icon: Package, to: '/warehouse/inward' },
  { kind: 'Navigate', label: 'Dispatch Dashboard', icon: Truck, to: '/warehouse/dispatch-dashboard' },
  { kind: 'Navigate', label: 'Production Orders', icon: Factory, to: '/production' },
  { kind: 'Navigate', label: 'BOM Dashboard', icon: Factory, to: '/manufacturing/boms' },
  { kind: 'Navigate', label: 'Projects', icon: CalendarDays, to: '/projects' },
  { kind: 'Navigate', label: 'Tasks', icon: CalendarDays, to: '/tasks' },
  { kind: 'Navigate', label: 'Service Tickets', icon: Wrench, to: '/service-tickets' },
  { kind: 'Navigate', label: 'TeleCaller', icon: Headphones, to: '/telecaller' },
  { kind: 'Navigate', label: 'Analytics', icon: BarChart3, to: '/analytics' },
  { kind: 'Navigate', label: 'AI Assistant', icon: Sparkles, to: '/ai' },
  { kind: 'Navigate', label: 'AI Credits', icon: Coins, to: '/credits' },
  { kind: 'Navigate', label: 'Settings', icon: FileText, to: '/settings' },

  // Create actions
  { kind: 'Create', label: 'New Proposal', icon: Plus, to: '/proposals/new' },
  { kind: 'Create', label: 'New Invoice', icon: Plus, to: '/invoices/new' },
  { kind: 'Create', label: 'New Sales Order', icon: Plus, to: '/sales-orders/new' },
  { kind: 'Create', label: 'New Purchase Order', icon: Plus, to: '/purchase-orders/new' },
  { kind: 'Create', label: 'New Service Ticket', icon: Plus, to: '/service-tickets/new' },

  // AI
  { kind: 'AI', label: 'Draft proposal for current deal', icon: Sparkles, cost: 5 },
  { kind: 'AI', label: 'Summarise this week', icon: Sparkles, cost: 3 },
  { kind: 'AI', label: 'Score a lead', icon: Sparkles, cost: 2 },
  { kind: 'AI', label: 'Generate GSTR-1 summary', icon: Sparkles, cost: 8 },
];

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onAiOpen?: () => void;
}

export function CommandPalette({ open, onClose, onAiOpen }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return STATIC_ITEMS;
    return STATIC_ITEMS.filter((it) =>
      it.label.toLowerCase().includes(query)
      || it.kind.toLowerCase().includes(query)
      || (it.meta ?? '').toLowerCase().includes(query),
    );
  }, [q]);

  // Reset highlight when filter changes / palette toggles
  useEffect(() => { setIdx(0); }, [q, open]);

  // Clear query on close
  useEffect(() => { if (!open) setQ(''); }, [open]);

  // Key handling
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIdx((i) => Math.min(filtered.length - 1, i + 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIdx((i) => Math.max(0, i - 1));
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[idx];
        if (!item) return;
        runItem(item);
      }
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered, idx, onClose]);

  function runItem(item: PaletteItem) {
    if (item.to) {
      navigate(item.to);
      onClose();
      return;
    }
    if (item.kind === 'AI' && onAiOpen) {
      onAiOpen();
      onClose();
      return;
    }
    onClose();
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex justify-center"
      style={{
        zIndex: 100,
        background: 'rgba(27,23,19,.38)',
        backdropFilter: 'blur(4px)',
        paddingTop: 120,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col overflow-hidden rounded-xl border shadow-2xl"
        style={{
          width: 580, maxHeight: 480,
          background: 'var(--ivory-1)',
          borderColor: 'var(--line)',
          boxShadow: 'var(--sh-4)',
        }}
      >
        {/* Search input */}
        <div
          className="flex items-center gap-2.5 border-b px-4 py-3"
          style={{ borderColor: 'var(--line)' }}
        >
          <Search size={16} style={{ color: 'var(--ink-3)' }} />
          <input
            autoFocus
            type="text"
            placeholder="Search or jump to…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 border-none bg-transparent text-[15px] outline-none"
            style={{ color: 'var(--ink-0)' }}
          />
          <span
            className="font-mono rounded px-1.5 py-0.5 text-[11px]"
            style={{
              background: 'var(--ivory-2)',
              color: 'var(--ink-3)',
            }}
          >esc</span>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-1.5 py-1.5">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-[13px]" style={{ color: 'var(--ink-3)' }}>
              No matches.
            </div>
          )}
          {filtered.map((item, i) => {
            const active = i === idx;
            const IconCmp = item.icon;
            const isAI = item.kind === 'AI';
            return (
              <button
                key={`${item.kind}-${item.label}`}
                type="button"
                onMouseEnter={() => setIdx(i)}
                onClick={() => runItem(item)}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px]"
                style={{
                  background: active ? 'var(--ivory-2)' : 'transparent',
                  color: 'var(--ink-1)',
                }}
              >
                <IconCmp
                  size={15}
                  style={{ color: isAI ? 'var(--saffron-600)' : 'var(--ink-3)', flexShrink: 0 }}
                />
                <span
                  className="w-[60px] flex-shrink-0 text-[10px] font-medium uppercase tracking-wider"
                  style={{ color: 'var(--ink-3)' }}
                >
                  {item.kind}
                </span>
                <span className="flex-1 truncate">{item.label}</span>
                {typeof item.cost === 'number' && (
                  <span
                    className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
                    style={{
                      background: 'var(--saffron-50)',
                      color: 'var(--saffron-600)',
                      flexShrink: 0,
                    }}
                  >
                    <Sparkles size={11} /> {item.cost}
                  </span>
                )}
                {item.hint && (
                  <span
                    className="font-mono text-[10px]"
                    style={{ color: 'var(--ink-3)' }}
                  >{item.hint}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="flex items-center gap-3 border-t px-3.5 py-2 text-[11px]"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-3)' }}
        >
          <span>↑↓ navigate</span>
          <span>↵ select</span>
          <div className="flex-1" />
          <span className="font-mono">⌘K to open</span>
        </div>
      </div>
    </div>
  );
}
